import {
  type ApprovalRollupSignal,
  approvalRollupSignalSchema,
} from "@mg/schemas/src/christo/approvalEvent";
import { CaretDown, CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ReviewListCard } from "./ReviewListCard";

import { type GetProjectReviewsResponse } from "../../../services/askPunttProjectFeed.types";
import { toTitleCase } from "../../../utils/titleCase";
import { approvalRollupLabel } from "../utils/approvalRollupLabel";
import { resolveReviewVersionId } from "../utils/resolveReviewVersionId";

/**
 * Right-hand REVIEWS panel: an overall status chip (e.g. IN REVIEW) plus a
 * card per active asset under review. Participation actions live in the shared
 * chat composer (`ApprovalRequestComposer`), not under these cards.
 */
export function ProjectReviewsPanel({
  data,
  isLoading = false,
  onOpenReview,
  isReviewUnread,
  open: openProp,
  onOpenChange,
  approvalRollupSignal,
  project = null,
  highlightedVersionId = null,
}: {
  data?: GetProjectReviewsResponse;
  isLoading?: boolean;
  onOpenReview?: (item: GetProjectReviewsResponse["reviews"][number]) => void;
  isReviewUnread?: (ticketId: string) => boolean;
  /** Controlled expand state for the reviews section header. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** @deprecated Kept for call-site compatibility; unused after in-chat approvals. */
  projectId?: string;
  /** Used to resolve review rows to file-version ids for approval-card highlight. */
  project?: {
    files?: {
      fileId: string;
      versions?: { versionId: string; versionNumber: number }[];
    }[];
  } | null;
  /** @deprecated Kept for call-site compatibility; unused after in-chat approvals. */
  resolveName?: (userId: string) => string;
  /** Advisory project-wide participation signal (soft — never a gate). */
  approvalRollupSignal?: ApprovalRollupSignal | string | null;
  /** @deprecated Kept for call-site compatibility; unused after in-chat approvals. */
  currentUserId?: string | null;
  /**
   * File-version id of the in-chat approval card currently hovered/focused.
   * Must match a single version — never highlight every version of a file.
   */
  highlightedVersionId?: string | null;
}) {
  const { t } = useTranslation("ui");
  const [openInternal, setOpenInternal] = useState(true);
  const open = openProp ?? openInternal;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (openProp === undefined) setOpenInternal(next);
  };
  const reviews = data?.reviews ?? [];
  const status = data?.status;
  const parsedRollup =
    approvalRollupSignalSchema.safeParse(approvalRollupSignal);
  const rollupSignal = parsedRollup.success ? parsedRollup.data : null;
  const showRollupChip = rollupSignal != null;

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white ${open ? "h-full flex-1" : "shrink-0"}`}
      aria-label={t("reviews")}
      data-testid="project-reviews-panel"
      data-panel-expanded={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={t("toggleReviews")}
        data-testid="project-reviews-toggle"
        className="flex w-full shrink-0 items-center gap-2 bg-base-white px-4 py-3 text-left"
      >
        <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
          {t("reviews")}
        </h2>
        <CaretDown
          size={14}
          aria-hidden="true"
          className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>

      {open && (
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-puntt-neutral-gray-5 px-4 pb-4 pt-3 [scrollbar-width:thin]"
          data-testid="project-reviews-scroll"
        >
          {showRollupChip ? (
            <p
              className="mb-3 text-[11px] font-medium text-puntt-neutral-gray-9"
              data-testid="project-rollup-signal"
            >
              {approvalRollupLabel(rollupSignal)}
            </p>
          ) : status ? (
            <p
              className="mb-3 text-[11px] font-medium text-puntt-neutral-gray-9"
              data-testid="project-reviews-status"
            >
              {toTitleCase(status.toLowerCase())}
            </p>
          ) : null}

          {isLoading ? (
            <div
              className="flex items-center justify-center py-4"
              data-testid="project-reviews-loading"
            >
              <CircleNotch
                size={18}
                weight="bold"
                className="animate-spin text-puntt-neutral-gray-9"
              />
            </div>
          ) : reviews.length === 0 ? (
            <p
              className="text-[13px] leading-relaxed text-puntt-neutral-gray-10"
              data-testid="project-reviews-empty"
            >
              {t("projectReviewsPanel.noActiveReviews")}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {reviews.map((item) => {
                const hasTicket = Boolean(item.ticketId);
                const inFlight = item.status === "in_review";
                const itemVersionId =
                  item.fileId != null
                    ? resolveReviewVersionId(project, {
                        fileId: item.fileId,
                        versionNumber: item.versionNumber,
                      })
                    : null;
                const isHighlighted =
                  highlightedVersionId != null &&
                  itemVersionId != null &&
                  itemVersionId === highlightedVersionId;

                return (
                  <ReviewListCard
                    key={item.id}
                    data-testid={`project-review-card-${item.id}`}
                    hasUnread={
                      hasTicket &&
                      !inFlight &&
                      (isReviewUnread?.(item.ticketId!) ?? false)
                    }
                    unreadTestId={`campaign-artifact-unread-${item.id}`}
                    isHighlighted={isHighlighted}
                    item={{
                      id: item.id,
                      name: item.name,
                      versionNumber: item.versionNumber,
                      thumbnailUrl: item.thumbnailUrl,
                      status: item.status,
                      aiCommentCount: item.aiCommentCount,
                      humanCommentCount: item.humanCommentCount,
                      disabled: !hasTicket || inFlight,
                      isGenerating: inFlight,
                      approval: item.approval
                        ? {
                            decision: item.approval.decision,
                            reviewerName: item.approval.reviewerName,
                            at: item.approval.at
                              ? new Date(item.approval.at)
                              : undefined,
                          }
                        : null,
                    }}
                    testIdPrefix="project-review-card"
                    onClick={() => {
                      if (hasTicket && !inFlight) onOpenReview?.(item);
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
